/**
 * SAD-832: the program Roles "Add to Team" / Program Lead picker
 * (ProgramRolesTab.tsx) used to offer only Delivery > Contacts rows — a
 * just-invited tenant member had no contact yet, so they could never be
 * picked until someone separately created one for them under Delivery >
 * Contacts. The picker now also offers the tenant's platform user directory
 * (the same source PlatformUserPicker.tsx reads), merged with contacts so
 * each person appears once: a user already linked to a contact (matched by
 * platformUserId) shows as that contact, never as a second "user" row.
 *
 * Picking a contactless user sends userId instead of contactId on create;
 * ryugu resolves it server-side (delivery_program_role_user_resolution.go).
 */ const contactLabel = (c)=>`${c.firstName ?? ''} ${c.lastName ?? ''}`.trim() || c.email || 'Unnamed contact';
const userLabel = (u)=>u.name?.trim() || u.email || u.userId;
/**
 * Merges contacts and platform users into one pick list. Every contact is
 * offered as-is; a user is offered only when no offered contact already
 * links to them (platformUserId match) — that person is already represented
 * by their contact row.
 */ export function mergePeopleForPicker(contacts, users) {
    const linkedUserIds = new Set(contacts.map((c)=>c.platformUserId).filter((id)=>!!id));
    const contactRows = contacts.map((c)=>({
            key: `contact:${c.id}`,
            kind: 'contact',
            id: c.id,
            label: contactLabel(c)
        }));
    const userRows = users.filter((u)=>!linkedUserIds.has(u.userId)).map((u)=>({
            key: `user:${u.userId}`,
            kind: 'user',
            id: u.userId,
            label: userLabel(u)
        }));
    return [
        ...contactRows,
        ...userRows
    ];
}
/** Parses a picker's composite Select value back into kind + id. */ export function parsePersonKey(key) {
    if (key.startsWith('contact:')) {
        const id = key.slice('contact:'.length);
        return id ? {
            kind: 'contact',
            id
        } : null;
    }
    if (key.startsWith('user:')) {
        const id = key.slice('user:'.length);
        return id ? {
            kind: 'user',
            id
        } : null;
    }
    return null;
}
